import { InvoicePaymentFilter, InvoicePaymentStatus } from '../../models/invoice-payment-status';
import { Button, Form, Modal, Spinner, Tab } from 'react-bootstrap';
import Select from 'react-select';
import { NoDataComponent } from '../NoDataComponent';
import {
    faCircle,
    faLongArrowAltRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    getCreditNotePDF,
    getInvoicePDF,
    getInvoicePDFWithFormValues,
    getInvoicePreview,
    deleteInvoices,
    useEditInvoices,
    useGetInvoices,
} from '../../utils/clients-hooks';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import InfiniteScroll from 'react-infinite-scroll-component';
import { OptionsContext } from '../../context/OptionsContext';
import { EditInvoice, Invoice } from '../../models/table-models';
import { getInvoiceColor, getLetter, getCurrencySymbol } from '../../utils/helper-functions';
import { BranchListItem, InvoicePreview } from '../../models/dashboard-models';
import { BranchesContext } from '../../context/BranchesContext';
import { invoiceStatuses } from '../../utils/Enums/InvoiceFields';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../CustomToastContent';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import { AuthContext } from '../../context/AuthContext';
import { useGetAllTurnovers } from '../../utils/users-hooks';
import EditableInvoice from '../invoiceHtml/EditableInvoice';
import { InfoModal } from '../InfoModal';
import dateFormat from 'dateformat';

interface IGETInvoices {
    'pager.page': number;
    'pager.itemsPerPage': number;
    filter: {
        id: string | undefined;
        tags: never[];
        search: string;
        type: InvoicePaymentFilter;
    };
}

export const Payments = () => {
    const [page, setPage] = useState(1);

    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: newRequestTranslation } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const { branches } = useContext(BranchesContext);
    const [filterStatus, setFilterStatus] = useState<InvoicePaymentFilter>(InvoicePaymentStatus.Unpaid);

    const [branch, setBranch] = useState<BranchListItem | undefined>();
    const [getInvoices, fetchNewInvoices] = useAsyncResource(useGetInvoices, {
        'pager.page': page,
        'pager.itemsPerPage': 30,
        filter: {
            id: branch?.id ? String(branch?.id) : undefined,
            tags: [],
            search: '',
            type: filterStatus,
        },
    });
    const { paidInvoices, pager } = getInvoices();
    const [invoices, setInvoices] = useState([] as Invoice[]);

    const loadMore = () => {
        setPage(() => page + 1);
    };
    const fetchHook = () => {
        resourceCache(useGetInvoices).clear();
        // fetchNewInvoices({}, true);
        fetchNewInvoices({} as IGETInvoices);
    };

    useEffect(() => {
        setInvoices((prevInvoices) => prevInvoices.concat([...paidInvoices]));
    }, [page]);

    const onBranchChange = (branch?: BranchListItem) => {
        setBranch(branch);
    };

    const setInvoiceFilter = (status: InvoicePaymentFilter) => {
        resourceCache(useGetInvoices).clear();
        setFilterStatus(status);
    };

    useEffect(() => {
        setPage(1);
        setInvoices([...paidInvoices]);
    }, [filterStatus, branch]);

    return (
        <>
            <Tab.Pane eventKey='second' className=' w-100 position-relative '>
                <div className=' w-100 position-relative overflow-auto h-100' id='infiniteInvoicesContainer'>
                    <Select
                        className='m-2'
                        maxMenuHeight={150}
                        isClearable
                        onChange={(option) => {
                            onBranchChange(option ?? undefined);
                        }}
                        noOptionsMessage={() => {
                            return <h6>{t('noBranchesFound')}</h6>;
                        }}
                        value={branch as unknown as BranchListItem}
                        options={branches}
                        placeholder={newRequestTranslation('selectBranch')}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => `${option.id}`}
                        blurInputOnSelect
                    ></Select>
                    <Select
                        className='m-2'
                        options={invoiceStatuses.map(status => ({ name: status, value: status }))}
                        value={{ name: filterStatus, value: filterStatus }}
                        getOptionLabel={({ name }) => t(name)}
                        getOptionValue={({ value }) => value}
                        onChange={(option) => {
                            if (option) setInvoiceFilter(option.value);
                        }}
                    />

                    <InfiniteScroll
                        dataLength={invoices.length}
                        next={loadMore}
                        style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                        hasMore={pager.totalCount > invoices.length}
                        loader={<div className='loader'>Loading ...</div>}
                        endMessage={
                            <p className='text-center pt-2'>
                                <b>{t(`noInvoices`)}</b>
                            </p>
                        }
                        scrollableTarget='infiniteInvoicesContainer'
                    >
                        {invoices.length > 0 ? (
                            <div className='mx-2 border border-dark'>
                                {invoices.map((invoice, index) => (
                                    <DashboardInvoice
                                        {...{ invoice, fetchHook, selectedId: invoice.id }}
                                        key={index}
                                    ></DashboardInvoice>
                                ))}
                            </div>
                        ) : (
                            <div className='h-100 d-flex align-items-center w-100 px-2'>
                                <NoDataComponent noDataText={t('payments')}></NoDataComponent>
                            </div>
                        )}
                    </InfiniteScroll>
                </div>
            </Tab.Pane>
        </>
    );
};

export const DashboardInvoice = ({
    invoice,
    fetchHook,
    selectedId,
}: {
    invoice: Invoice;
    fetchHook: () => void;
    selectedId?: number;
}) => {
    const { allConfigurations } = useContext(OptionsContext);
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const color = getInvoiceColor(invoice, allConfigurations.timerDays.invoiceDays);
    const { loggedUser } = useContext(AuthContext);

    const [showModal, setShowModal] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [forDelete, setForDelete] = useState(false);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);
    const submitRef = useRef<HTMLButtonElement>(null);
    const toggleShowModal = () => {
        setShowModal((prev) => !prev);
    };

    const [invoicePreviewData, setInvoicePreviewData] = useState<InvoicePreview>({} as InvoicePreview);
    const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);

    useEffect(() => {
        setInvoicePreviewLoading(true);
        getInvoicePreview(
            invoice.document?.id,
            invoice.documentType != 'invoice' && invoice.documentType != 'receipt'
        ).then((value) => setInvoicePreviewData(value))
            .finally(() => setInvoicePreviewLoading(false));
    }, [loading, invoice]);

    useEffect(() => {
        if (showModal) {
            setInvoicePreviewLoading(true);
            getInvoicePreview(
                invoice.document?.id,
                invoice.documentType != 'invoice' && invoice.documentType != 'receipt',
            ).then((value) => setInvoicePreviewData(value))
                .finally(() => setInvoicePreviewLoading(false));
        }
        // if (!showModal && submitRef && submitRef.current) {
        //     submitRef.current?.click(); //needs to be added back after edit invoice has been fixed
        // }
    }, [showModal]);

    const print = async () => {
        const pdfFile = await getInvoicePDF(invoice?.document?.id, loggedUser?.id);
        if (pdfFile) {
            const pdf = URL.createObjectURL(pdfFile);
            window.open(pdf, '_blank');
            if (hasChanges) {
                resourceCache(useGetInvoices).clear();
                resourceCache(useGetAllTurnovers).clear();
                resourceCache(getInvoicePreview).clear();
                fetchHook();
            }
            return true;
        } else {
            toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText />, toastOptionsTopRight);
            return false;
        }
    };

    const editableInvoiceRef = useRef<any>();

    const handleSubmit = async (formValue: EditInvoice) => {
        setLoading(true);
        const invoiceData = editableInvoiceRef.current?.getInvoiceData();
        const pdfFile = await getInvoicePDFWithFormValues(invoiceData, invoice?.document?.id).catch((error) => {
            return null;
        });
        // const pdfFile = await getInvoicePDF(invoice?.id, loggedUser?.id);
        if (pdfFile) {
            const pdf = URL.createObjectURL(pdfFile);
            window.open(pdf, '_blank');
            setLoading(false);
            if (hasChanges) {
                resourceCache(useGetInvoices).clear();
                resourceCache(useGetAllTurnovers).clear();
                resourceCache(getInvoicePreview).clear();
                fetchHook();
            }
            return true;
        } else {
            setLoading(false);
            return false;
        }
    };

    const handleCreditNoteSubmit = async () => {
        setLoading(true);
        const invoiceData = editableInvoiceRef.current?.getInvoiceData();
        const pdfFile = await getCreditNotePDF(invoiceData, invoice?.document?.id, loggedUser?.id).catch((error) => {
            return null;
        });
        resourceCache(useGetInvoices).clear();
        if (pdfFile) {
            const pdf = URL.createObjectURL(pdfFile);
            window.open(pdf, '_blank');
            setLoading(false);
            return true;
        } else {
            setLoading(false);
            return false;
        }
    };
    const useSubmitHandlerEdit = async (formValue: EditInvoice) => {
        setLoading(true);
        await useEditInvoices(formValue);
        resourceCache(useGetInvoices).clear();
        resourceCache(useGetAllTurnovers).clear();
        fetchHook();

        if (showModal) await print();
        setLoading(false);
    };

    const DeleteInvoice = async (fields: Invoice) => {
        await deleteInvoices(fields, comment);
        setShowModal(false);
        resourceCache(useGetInvoices).clear();
        resourceCache(useGetAllTurnovers).clear();
        fetchHook();

        toast.success(<CustomToastContent content={[t('invoiceDeleted')]} />, toastOptionsTopRight);
    };

    return (
        <>
            <div
                className={`d-flex w-100 p-1 py-2 align-items-center border-bottom border-dark hover`}
                onClick={toggleShowModal}
            >
                <div className='w-100 d-flex align-items-center justify-content-between'>
                    {invoice.document?.creditNoteNumber && (
                        <FontAwesomeIcon
                            icon={faLongArrowAltRight}
                            {...{ color: 'red' }}
                            className='mx-1'
                        ></FontAwesomeIcon>
                    )}
                    <span className='text-secondary'>
                        {dateFormat(invoice.fromDate, 'dd-mm-yyyy')} -{' '}
                        {dateFormat(invoice.duePaymentDate, 'dd-mm-yyyy')}: {(+invoice.total / 100).toFixed(2)}{getCurrencySymbol(invoice.currency)}
                    </span>
                    <FontAwesomeIcon icon={faCircle} {...{ color }} className='mx-1'></FontAwesomeIcon>
                </div>
            </div>
            <Modal show={showModal} onHide={() => setShowModal(false)} size='lg' centered>
                <Modal.Header closeButton>
                    <Modal.Title id='contained-modal-title-vcenter'>
                        {t('Details for') +
                            ' ' +
                            t(invoice.document?.documentType ? invoice.document?.documentType : '') +
                            ' ' +
                            (invoice.document?.invoiceNumber
                                ? '№' + invoice.document?.invoiceNumber
                                : invoice.document?.documentType == 'invoice'
                                    ? '(' + t('Missing invoice number') + ')'
                                    : '(' + t('Unpaid debt') + ')')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showModal && invoicePreviewLoading && (
                        <div className='d-flex justify-content-center'>
                            <Spinner animation='border' />
                        </div>
                    )}
                    {showModal && !invoicePreviewLoading && invoicePreviewData?.items && (
                        <EditableInvoice
                            {...{ invoicePreviewData }}
                            setHasChanges={setHasChanges}
                            ref={editableInvoiceRef}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <Button onClick={() => setShowModal(false)}>{t('close')}</Button>
                    {invoice.document?.invoiceNumber == null && (
                        <Button className='btn-danger' onClick={() => setForDelete(true)}>
                            {t('delete')}
                        </Button>
                    )}
                    {invoice.document?.invoiceNumber &&
                        invoice.document?.documentType &&
                        invoice.document?.documentType.toLowerCase() === 'invoice' &&
                        invoice.document?.paymentType &&
                        invoice.document?.paymentType.toLowerCase() === 'bank' &&
                        !invoice.document?.creditNoteNumber &&
                        (loading ? (
                            <Spinner animation={'border'}></Spinner>
                        ) : (
                            <Button form='form' type='submit' onClick={() => handleCreditNoteSubmit()}>
                                {t('printCreditNote')}
                            </Button>
                        ))}
                    {invoice.document?.invoiceNumber &&
                        invoice.document?.creditNoteNumber &&
                        (loading ? (
                            <Spinner animation={'border'}></Spinner>
                        ) : (
                            <Button form='form' type='submit' onClick={() => handleCreditNoteSubmit()}>
                                {t('viewCreditNote')}
                            </Button>
                        ))}
                    {loading ? (
                        <Spinner animation={'border'}></Spinner>
                    ) : (
                        <Button
                            ref={submitRef}
                            form='form'
                            type='submit'
                            onClick={() =>
                                editableInvoiceRef.current?.getInvoiceData()?.id === 'КРЕДИТНО ИЗВЕСТИЕ' ||
                                    editableInvoiceRef.current?.getInvoiceData()?.id === 'СТОРНО'
                                    ? handleCreditNoteSubmit()
                                    : handleSubmit({} as EditInvoice)
                            }
                        >
                            {t('print')}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
            {forDelete && (
                <InfoModal
                    isDisabled={comment.length < 1}
                    show={forDelete}
                    onHide={(isDelete: boolean) => {
                        if (isDelete) {
                            DeleteInvoice(invoice);
                        }
                        setComment('');
                        return setForDelete(false);
                    }}
                >
                    <span className='w-100 text-secondary'>{`*${t('commentRequired')}`}</span>
                    <Form.Control
                        placeholder={t('comment')}
                        name={'freetext'}
                        onChange={(e) => setComment(e.currentTarget.value)}
                        value={comment}
                    />
                </InfoModal>
            )}
        </>
    );
};
